import React from 'react';
import { getWindow } from '../dom/getWindow';

declare global {
  const VERSION: string;
  const COMMITHASH: string;
  const BRANCH: string;
  const ENVIRONMENT: string;
  const TIMESTAMP: string;
  const PACKAGE_VERSION: string;

  interface Window {
    Globals: typeof globals;
  }
}

export const globals = {
  getVersion() {
    return {
      version: typeof window !== 'undefined' ? VERSION : undefined,
      commit: typeof window !== 'undefined' ? COMMITHASH : undefined,
      branch: typeof window !== 'undefined' ? BRANCH : undefined,
      env: typeof window !== 'undefined' ? ENVIRONMENT : undefined,
      timestamp: typeof window !== 'undefined' ? TIMESTAMP : undefined,
      package: typeof window !== 'undefined' ? PACKAGE_VERSION : undefined,
    };
  },
};

if (typeof window !== 'undefined') {
  getWindow().Globals = globals;
}

/**
 * Indica si el debugDispatcher fue activado, lo que en muchos casos indica que
 * estamos en ambiente de desarrollo
 */
export function isDebugDispatcherEnabled() {
  return globals.getVersion().env === 'development';
}

type TWindowDDispatch = (action: string, ...parameters: unknown[]) => void;
export type TDispatchCallback = (parameters: unknown[]) => unknown;
interface IStoredCallback {
  (parameters: unknown[]): unknown;
  help: () => void;
}

export const debugDispatcher = new (class DDispatcher {
  callbacks: Record<string, IStoredCallback[]> = {};

  actions: Record<string, () => void> = {
    shout: () => {
      console.log(Object.keys(this.callbacks));
    },
  };

  on(
    action: string,
    cb: TDispatchCallback,
    help: string | React.ReactNode,
    onlyDevelop = false,
  ) {
    if (!isDebugDispatcherEnabled() && onlyDevelop) return () => {};

    if (Object.keys(this.actions).includes(action))
      throw new Error(
        `The action ${action} is a reserved word for the dispatcher.`,
      );
    if (!this.callbacks[action]) this.callbacks[action] = [];
    const actionMethod = Object.assign(cb, {
      help: () => {
        if (typeof help === 'string') console.info(help);
      },
    });
    this.callbacks[action].push(actionMethod);
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    this[action] = Object.assign(
      (...props: unknown[]) => {
        this.emit(action, ...props);
      },
      {
        help: () => {
          if (typeof help === 'string') console.info(help);
        },
      },
    );
    return () => {
      this.off(action, cb);
    };

    // return () => {};
  }

  off(action: string, cb: TDispatchCallback) {
    this.callbacks[action] = this.callbacks[action].filter((current) => {
      return current !== cb;
    });
  }

  emit: TWindowDDispatch = (action, ...parameters) => {
    try {
      if (this.actions[action]) this.actions[action]();
      else {
        if ((this.callbacks[action] ?? []).length === 1)
          return this.callbacks[action]?.[0]?.(parameters);

        (this.callbacks[action] ?? []).forEach((cb) => {
          return cb(parameters);
        });
      }
      return null;
    } catch (e) {
      console.error('DD', e);
    }
  };
})();

declare global {
  interface Window {
    adt: typeof debugDispatcher;
    dd: typeof debugDispatcher & Record<string, () => unknown>;
  }
}

if (typeof window !== 'undefined') {
  getWindow().dd = debugDispatcher as typeof debugDispatcher &
    Record<string, () => unknown>;
  getWindow().adt = debugDispatcher;
}
